import { useCallback, useEffect, useState } from 'react';
// @mui
import { Button, MenuItem } from '@mui/material';
import {
  DataGridPremium,
  GridColDef,
  GridRenderCellParams,
  GridSortModel,
} from '@mui/x-data-grid-premium';
// hooks
import { fDate } from 'src/utils/format-time';
import { fCurrency } from 'src/utils/format-number';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
// components
import { ConfirmDialog } from 'src/components/custom-dialog';
import { useBoolean } from 'src/hooks/use-boolean';
import Iconify from 'src/components/iconify';
import { getMembershipPrices } from 'src/redux/slices/membership-type-price';
import { useDispatch, useSelector } from '../../../redux/store';
//
import { useLocales } from '../../../locales';
import { ContingentTypeDto, MembershipTypePriceDto } from '../../../api';

// ----------------------------------------------------------------------

type Props = {
  membershipType: ContingentTypeDto;
};

export default function PersonalTrainingDetailsPricing({ membershipType }: Props) {
  const { t } = useLocales();

  const popover = usePopover();

  const confirmDeletion = useBoolean();

  const dispatch = useDispatch();

  const prices = useSelector((state) => state.membershipTypePrice.currentMembershipPrices);
  const totalNumber = useSelector((state) => state.membershipTypePrice.totalPrices);
  const pricesLoading = useSelector((state) => state.membershipTypePrice.isLoading);

  const [selectedSignUp, setSelectedSignUp] = useState<MembershipTypePriceDto | null>(null);

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>, row: MembershipTypePriceDto) => {
    setSelectedSignUp(row);
    popover.onOpen(event);
  };

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 5,
    page: 0,
  });

  const [queryOptions, setQueryOptions] = useState([
    { field: 'validFrom', sort: 'desc' },
  ] as GridSortModel);

  const handleSortModelChange = useCallback((sortModel: GridSortModel) => {
    setQueryOptions(sortModel);
  }, []);

  useEffect(() => {
    dispatch(
      getMembershipPrices({
        pageNumber: paginationModel.page,
        pageSize: paginationModel.pageSize,
        sortBy: queryOptions[0]?.field,
        direction: queryOptions[0]?.sort,
        id: membershipType.id!,
      })
    );
  }, [dispatch, membershipType, paginationModel, queryOptions]);

  const rows = prices.map((x) => ({ ...x }));

  const columns: GridColDef<(typeof rows)[number]>[] = [
    {
      field: 'priceAmount',
      headerName: t('Price'),
      flex: 1,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<any, any>) => (
        <span>{fCurrency(params.row.priceAmount, params.row.priceCurrency)}</span>
      ),
    },
    {
      field: 'validFrom',
      headerName: t('Valid from'),
      flex: 1,
      display: 'flex',
      valueFormatter: (value) => fDate(value),
    },
    {
      field: 'validTo',
      headerName: t('Valid to'),
      flex: 1,
      display: 'flex',
      valueFormatter: (value) => fDate(value),
    },
    {
      field: 'taxRate',
      headerName: t('VAT'),
      flex: 1,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<any, any>) => (
        <span>
          {params.row.taxRate?.value
            ? `${params.row.taxRate?.inclusive ? t('Incl.') : t('Excl.')} ${
                params.row.taxRate?.value
              }%`
            : '-'}
        </span>
      ),
    },
    {
      field: 'updatePriceOnExistingMemberships',
      headerName: t('Price increase'),
      flex: 1,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<any, any>) => (
        <span>{params.row.updatePriceOnExistingMemberships ? t('Yes') : t('No')}</span>
      ),
    },
    /* {
        field: 'actions',
        headerName: t('Actions'),
        renderHeader: () => '',
        sortable: false,
        disableColumnMenu: true,
        align: 'right',
        display: 'flex',
        renderCell: (params: GridRenderCellParams<any, any>) => (
          <IconButton
            color={popover.open ? 'inherit' : 'default'}
            onClick={(e) => handleOpenPopover(e, params.row)}
          >
            <Iconify icon="eva:more-vertical-fill" />
          </IconButton>
        ),
      }, */
  ];

  const handleDelete = useCallback(
    (item: number) => {
      const request = async () => {};
      request();

      confirmDeletion.onFalse();
    },
    [confirmDeletion]
  );

  return (
    <>
      <DataGridPremium
        pagination
        pageSizeOptions={[5, 10, 25]}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        onSortModelChange={handleSortModelChange}
        rows={rows}
        columns={columns}
        rowCount={totalNumber}
        loading={pricesLoading}
        initialState={{
          sorting: {
            sortModel: queryOptions,
          },
        }}
        paginationMode="server"
        checkboxSelection={false}
        disableRowSelectionOnClick
        showToolbar
      />

      <CustomPopover open={popover.open} onClose={popover.onClose} arrow="top-right">
        <MenuItem sx={{ color: 'error.main' }} onClick={confirmDeletion.onTrue}>
          <Iconify icon="solar:trash-bin-trash-bold" />
          {t('Remove')}
        </MenuItem>
      </CustomPopover>

      <ConfirmDialog
        open={confirmDeletion.value}
        onClose={confirmDeletion.onFalse}
        title={t('Delete')}
        content={<>{t('Are you sure want to delete the selected price?')}</>}
        action={
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              handleDelete(selectedSignUp!.id!);
            }}
          >
            {t('Remove')}
          </Button>
        }
      />
    </>
  );
}
